// trace:FEAT-0006
/**
 * Obligor API service calling rms-aggregator endpoints.
 *
 * All obligor data is sourced from the rms-aggregator BFF.
 * The UI never calls rms-identity, rms-financial, or rms-obligation directly.
 * Access control is enforced by the BFF; the UI trusts the BFF response.
 */
import type { ApiClient } from "./client";
import type {
  ObligorSearchQuery,
  ObligorSearchResponse,
  ObligorDetail,
  TypeaheadResponse,
} from "./types/obligor";

function toSearchParams(query: ObligorSearchQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.name) params.set("name", query.name);
  if (query.tin) params.set("obligor_number", query.tin); // trace:TASK-0393
  if (query.page_size !== undefined)
    params.set("page_size", String(query.page_size));
  return params;
}

export interface ObligorService {
  searchObligors(
    query: ObligorSearchQuery,
    signal?: AbortSignal,
  ): Promise<ObligorSearchResponse>;
  getObligorDetail(
    obligorId: string,
    signal?: AbortSignal,
  ): Promise<ObligorDetail>;
  typeaheadObligors(
    query: string,
    limit: number,
    signal?: AbortSignal,
  ): Promise<TypeaheadResponse>;
}

function extractApiDetail(err: unknown): Error {
  if (!(err instanceof Error)) return new Error(String(err));
  const colonIdx = err.message.indexOf(": ");
  if (colonIdx === -1) return err;
  try {
    const body: unknown = JSON.parse(err.message.slice(colonIdx + 2));
    if (
      typeof body === "object" &&
      body !== null &&
      "detail" in body &&
      typeof (body as { detail: unknown }).detail === "string"
    ) {
      return new Error((body as { detail: string }).detail);
    }
  } catch {
    // Body wasn't JSON — return original error
  }
  return err;
}

export function createObligorService(client: ApiClient): ObligorService {
  return {
    async searchObligors(query, signal) {
      const params = toSearchParams(query);
      try {
        return await client.fetch<ObligorSearchResponse>(
          `/api/v1/aggregator/obligors/search?${params}`,
          { signal },
        );
      } catch (err: unknown) {
        throw extractApiDetail(err);
      }
    },

    async getObligorDetail(obligorId, signal) {
      return client.fetch<ObligorDetail>(
        `/api/v1/aggregator/obligors/${obligorId}`,
        { signal },
      );
    },

    async typeaheadObligors(query, limit, signal) {
      const params = new URLSearchParams();
      params.set("q", query);
      params.set("limit", String(limit));
      try {
        return await client.fetch<TypeaheadResponse>(
          `/api/v1/aggregator/obligors/typeahead?${params}`,
          { signal },
        );
      } catch (err: unknown) {
        throw extractApiDetail(err);
      }
    },
  };
}
